import Tooltip from "../../../components/Tooltip";
import _ from "lodash";
import React from "react";
import { useFormContext } from "react-hook-form";
import { Divider, Form } from "semantic-ui-react";
import { ControlledCheckbox, ControlledInput } from "../../../components/ControlledForm";
import { ControlledEmail } from "../../../components/ControlledEmail";
import { ContactInfoOptions } from "../../permits/types/ContactInfoOptions";
import { fieldError } from "../../../utils/functions";
import "./ContactFields.scss";
import { ControlledPhoneNumber } from "../../../components/global_components/form_components/ControlledPhoneNumber";
import ControlledAutofilledAddress from "../../../components/global_components/form_components/ControlledAutofilledAddress";

interface IContactFieldProps {
  showContactTypeField?: boolean;
  contactTypeValue?: string;
  showLicenseAndCompanyFields?: boolean;
  showMailingAddressHeader?: boolean;
  enteredCompanyName?: string;
  addressState?: string;
  showInviteUser?: boolean;
  namePrefix?: string; // prefix for form field names
  saveButton?: any;
  isSubmitterSection?: boolean;
  isApplicant?: boolean;
  physicalAddressPrefix?: string; // prefix for physical/home address fields
  defaultContactFields?: {
    contactType?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    mailAddress?: {
      address?: string;
      address2?: string;
      city?: string;
      state?: string;
      zip?: string;
      countryId?: string;
    };
    homeAddress?: {
      address?: string;
      address2?: string;
      city?: string;
      state?: string;
      zip?: string;
      countryId?: string;
    };
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    countryId?: string;
    physicalAddress?: string;
    physicalAddress2?: string;
    physicalCity?: string;
    physicalState?: string;
    physicalZip?: string;
    homeCountryId?: string;
    licenseNumber?: string;
    companyName?: string;
    shouldBeInvited?: boolean;
    sameMailAndPhysicalAddress?: boolean;
  };
  enteredValues?: {
    enteredfirstName?: string;
    enteredlastName?: string;
    enteredemail?: string;
    enteredphone?: string;
    enteredlicenseNumber?: string;
    enteredcompanyName?: string;
    enteredaddress?: string;
    enteredaddress2?: string;
    enteredcity?: string;
    enteredstate?: string;
    enteredzip?: string;
    enteredphysical_address?: string;
    enteredphysical_address2?: string;
    enteredphysical_city?: string;
    enteredphysical_state?: string;
    enteredphysical_zip?: string;
    enteredsameMailAndPhysicalAddress?: boolean;
  };
  visibilityOptions?: ContactInfoOptions;
  deleteButton?: any;
  showDivider?: boolean;
  // Input ref for focusing on the first input field, with proper typing
  inputRef?: React.RefObject<HTMLInputElement>;
  useNewAddressFields?: boolean;
  autofillCountry?: boolean;
}

/**
 * Provides controlled input fields for contact information (name, email, phone, address, etc.)
 */
export const ContactFieldsV2 = ({
  showContactTypeField = false,
  contactTypeValue,
  showLicenseAndCompanyFields = false,
  showInviteUser = false,
  deleteButton = null,
  isSubmitterSection = false,
  isApplicant = false,
  namePrefix,
  enteredValues = {},
  saveButton = null,
  defaultContactFields = {},
  visibilityOptions = {
    address: "optional",
    company_name: "optional",
    email_address: "optional",
    license_number: "optional",
    phone_number: "optional",
    first_name: "required",
    last_name: "required",
    contact_type: "optional",
  },
  showDivider = true,
  inputRef,
  autofillCountry = false,
}: IContactFieldProps) => {
  const {
    contactType,
    firstName,
    lastName,
    email,
    phone,
    mailAddress, // Default values from form data (e.g. permit application screen)
    homeAddress, // Default values from form data (e.g. permit application screen)
    address, // Default values from API (e.g. permit details screen)
    address2,
    city,
    state,
    zip,
    countryId,
    physicalAddress,
    physicalAddress2,
    physicalCity,
    physicalState,
    physicalZip,
    homeCountryId,
    licenseNumber,
    companyName,
    shouldBeInvited,
    sameMailAndPhysicalAddress,
  } = defaultContactFields;
  const {
    enteredfirstName,
    enteredlastName,
    enteredemail,
    enteredphone,
    enteredaddress,
    enteredaddress2,
    enteredcity,
    enteredstate,
    enteredzip,
    enteredphysical_address,
    enteredphysical_address2,
    enteredphysical_city,
    enteredphysical_state,
    enteredphysical_zip,
    enteredlicenseNumber,
    enteredcompanyName,
    enteredsameMailAndPhysicalAddress,
  } = enteredValues;
  const { control, errors, setError, getValues, clearErrors, watch } = useFormContext();

  // Takes in a name and combines it with a prefix (if one exists)
  const getNameWithPrefix = (name: string, prefix: string | undefined) => {
    return prefix ? `${prefix}.${name}` : name;
  };

  // Destructure visibility options
  const {
    address: addressVisibility,
    company_name: companyNameVisibility,
    email_address: emailVisibility,
    license_number: licenseVisibility,
    phone_number: phoneVisibility,
    first_name: firstNameVisibility,
    last_name: lastNameVisibility,
    contact_type: contactTypeVisibility,
  } = visibilityOptions;

  // Get required status of fields
  const isAddressRequired = addressVisibility === "required";
  const isCompanyNameRequired = companyNameVisibility === "required";
  const isEmailRequired = emailVisibility === "required";
  const isLicenseRequired = licenseVisibility === "required";
  const isPhoneRequired = phoneVisibility === "required";
  const isFirstNameRequired = firstNameVisibility === "required";
  const isLastNameRequired = lastNameVisibility === "required";
  const isContactTypeRequired = contactTypeVisibility === "required";

  // Get read-only status of fields
  const isAddressReadOnly = addressVisibility === "read_only";
  const isCompanyNameReadOnly = companyNameVisibility === "read_only";
  const isEmailReadOnly = emailVisibility === "read_only";
  const isLicenseReadOnly = licenseVisibility === "read_only";
  const isPhoneReadOnly = phoneVisibility === "read_only";
  const isFirstNameReadOnly = firstNameVisibility === "read_only";
  const isLastNameReadOnly = lastNameVisibility === "read_only";
  const isContactTypeReadOnly = contactTypeVisibility === "read_only";

  const showPhone = phoneVisibility !== "hidden";
  const showEmail = emailVisibility !== "hidden";
  const showLicense = licenseVisibility !== "hidden";
  const showCompany = companyNameVisibility !== "hidden";
  const showAddress = addressVisibility !== "hidden";

  // Get default address fields (can either come from backend (address, address2, etc.) or entered values (mailAddress))
  const defaultMailAddress = {
    address: address || mailAddress?.address || "",
    address2: address2 || mailAddress?.address2 || "",
    city: city || mailAddress?.city || "",
    state: state || mailAddress?.state || "",
    zip: zip || mailAddress?.zip || "",
    countryId: countryId || mailAddress?.countryId || "",
  };
  const defaultHomeAddress = {
    address: physicalAddress || homeAddress?.address || "",
    address2: physicalAddress2 || homeAddress?.address2 || "",
    city: physicalCity || homeAddress?.city || "",
    state: physicalState || homeAddress?.state || "",
    zip: physicalZip || homeAddress?.zip || "",
    countryId: homeCountryId || homeAddress?.countryId || "",
  };

  // Get default value of the toggle checkbox
  const defaultToggleValue =
    enteredsameMailAndPhysicalAddress === false
      ? false
      : ((enteredsameMailAndPhysicalAddress || sameMailAndPhysicalAddress) ?? true);

  const showHomeAddressFields = !watch(getNameWithPrefix("sameMailAndPhysicalAddress", namePrefix), defaultToggleValue);

  const showInviteCheckbox = showInviteUser && showEmail;
  const willShowLicenseAndCompanyFields =
    showLicenseAndCompanyFields || licenseVisibility !== "hidden" || companyNameVisibility !== "hidden";

  // Get default value of fields, depending on the field type, whether or not the user is an applicant, or if its on the applicant form section
  function getDefaultValue(
    fieldType: string,
    isApplicant: boolean,
    enteredValue: string | undefined,
    value: string | undefined,
  ) {
    // Do not let the entered value override any read only fields.
    if (isApplicant && fieldType === "read only") {
      return value || "";
    } else if (isSubmitterSection && isApplicant) {
      // Show the entered value OR the default values of the applicant
      return enteredValue || value || "";
    } else if (isSubmitterSection) {
      // If submitter section, and not applicant, then only show entered value, not default values
      return enteredValue || "";
    } else {
      // Fields not in the applicant form section maintain their values naturally, so no special logic with entered values is needed.
      return value || "";
    }
  }

  return (
    <div className='tw-mt-1'>
      {/* Role/Title and Delete (for ADDITIONAL CONTACTS) */}
      <div className='contact-role'>
        {showContactTypeField && (
          <Form.Field
            required={isContactTypeRequired}
            error={fieldError(errors, "contactType.message")}
            style={{ flexGrow: 1 }}
            className='tw-mb-4'
          >
            <ControlledInput
              name={getNameWithPrefix("contactType", namePrefix)}
              errors={errors}
              label='Role/Title'
              control={control}
              defaultValue={contactType || ""}
              required={isContactTypeRequired}
              readonly={isContactTypeReadOnly}
              inputRef={inputRef}
              autoComplete='organization-title'
            />
          </Form.Field>
        )}
        {!showContactTypeField && contactTypeValue && (
          <ControlledInput
            name={getNameWithPrefix("contactType", namePrefix)}
            errors={errors}
            control={control}
            type='hidden'
            defaultValue={contactTypeValue}
          />
        )}
      </div>

      {/* First/Last Name */}
      <Form.Group widths='equal'>
        <Form.Field className='tw-w-1/2' required error={fieldError(errors, "firstName.message")}>
          <ControlledInput
            name={getNameWithPrefix("firstName", namePrefix)}
            errors={errors}
            defaultValue={getDefaultValue("read only", isApplicant, enteredfirstName, firstName)}
            control={control}
            readonly={isFirstNameReadOnly}
            required={isFirstNameRequired || true} // first name is always required
            label='First Name'
            inputRef={showContactTypeField ? null : inputRef}
            autoComplete='given-name'
          />
        </Form.Field>

        <Form.Field className='tw-w-1/2' required error={fieldError(errors, "lastName.message")}>
          <ControlledInput
            name={getNameWithPrefix("lastName", namePrefix)}
            errors={errors}
            defaultValue={getDefaultValue("read only", isApplicant, enteredlastName, lastName)}
            control={control}
            readonly={isLastNameReadOnly}
            required={isLastNameRequired || true} // last name is always required
            label='Last Name'
            autoComplete='family-name'
          />
        </Form.Field>
      </Form.Group>

      {showEmail && (
        <Form.Field className='tw-w-auto' required={isEmailRequired} error={fieldError(errors, "email.message")}>
          <ControlledEmail
            readonly={isEmailReadOnly}
            name={getNameWithPrefix("email", namePrefix)}
            errors={errors}
            fieldName={"Email field"}
            label='Email'
            control={control}
            defaultValue={getDefaultValue("read only", isApplicant, enteredemail, email)}
            required={isEmailRequired}
          />
        </Form.Field>
      )}
      {showPhone && (
        <Form.Field className='tw-w-auto' required={isPhoneRequired} error={fieldError(errors, "phone.message")}>
          <ControlledPhoneNumber
            readonly={isPhoneReadOnly}
            name={getNameWithPrefix("phone", namePrefix)}
            errors={errors}
            label='Phone'
            control={control}
            defaultValue={getDefaultValue("editable", isApplicant, enteredphone, phone)}
            required={isPhoneRequired}
            placeholder=''
            autoComplete='tel'
          />
        </Form.Field>
      )}

      {/* License Number/Company Name */}
      {willShowLicenseAndCompanyFields && (showLicense || showCompany) && (
        <Form.Group>
          {showLicense && (
            <Form.Field
              className='tw-w-auto'
              required={isLicenseRequired}
              error={fieldError(errors, "licenseNumber.message")}
              style={{ flexGrow: 1 }}
            >
              <ControlledInput
                readonly={isLicenseReadOnly}
                name={getNameWithPrefix("licenseNumber", namePrefix)}
                label='License Number'
                control={control}
                defaultValue={getDefaultValue("editable", isApplicant, enteredlicenseNumber, licenseNumber)}
                required={isLicenseRequired}
                errors={errors}
              />
            </Form.Field>
          )}
          {showCompany && (
            <Form.Field
              className='tw-w-auto'
              required={isCompanyNameRequired}
              error={fieldError(errors, "companyName.message")}
              style={{ flexGrow: 1 }}
            >
              <ControlledInput
                readonly={isCompanyNameReadOnly}
                name={getNameWithPrefix("companyName", namePrefix)}
                label='Company Name'
                control={control}
                defaultValue={getDefaultValue("editable", isApplicant, enteredcompanyName, companyName)}
                required={isCompanyNameRequired}
                errors={errors}
              />
            </Form.Field>
          )}
        </Form.Group>
      )}
      {/* Mailing Address Fields */}
      {showAddress && (
        <>
          <ControlledAutofilledAddress
            name={getNameWithPrefix("mailAddress", namePrefix)}
            autofillCountry={autofillCountry}
            control={control}
            customAddressLabel='Mailing Address'
            showCountryField={true}
            defaultValue={{
              address: getDefaultValue("editable", isApplicant, enteredaddress, defaultMailAddress.address),
              address2: getDefaultValue("editable", isApplicant, enteredaddress2, defaultMailAddress.address2),
              city: getDefaultValue("editable", isApplicant, enteredcity, defaultMailAddress.city),
              state: getDefaultValue("editable", isApplicant, enteredstate, defaultMailAddress.state),
              zip: getDefaultValue("editable", isApplicant, enteredzip, defaultMailAddress.zip),
              countryId: getDefaultValue("editable", isApplicant, enteredzip, defaultMailAddress.countryId),
            }}
            required={isAddressRequired}
            readonly={isAddressReadOnly}
            errors={errors}
          />
          <ControlledCheckbox
            className='tw-mt-4'
            name={getNameWithPrefix("sameMailAndPhysicalAddress", namePrefix)}
            control={control}
            defaultValue={defaultToggleValue}
            toggle
            label={"Physical Address Is the Same as Mailing"}
          />
          {showHomeAddressFields && (
            <div className='tw-mt-4'>
              <ControlledAutofilledAddress
                name={getNameWithPrefix("homeAddress", namePrefix)}
                autofillCountry={autofillCountry}
                control={control}
                customAddressLabel='Physical Address'
                showCountryField={true}
                defaultValue={{
                  address: getDefaultValue(
                    "editable",
                    isApplicant,
                    enteredphysical_address,
                    defaultHomeAddress.address,
                  ),
                  address2: getDefaultValue(
                    "editable",
                    isApplicant,
                    enteredphysical_address2,
                    defaultHomeAddress.address2,
                  ),
                  city: getDefaultValue("editable", isApplicant, enteredphysical_city, defaultHomeAddress.city),
                  state: getDefaultValue("editable", isApplicant, enteredphysical_state, defaultHomeAddress.state),
                  zip: getDefaultValue("editable", isApplicant, enteredphysical_zip, defaultHomeAddress.zip),
                  countryId: getDefaultValue(
                    "editable",
                    isApplicant,
                    enteredphysical_zip,
                    defaultHomeAddress.countryId,
                  ),
                }}
                errors={errors}
              />
            </div>
          )}
        </>
      )}

      {/* Checkbox for inviting a contact */}
      {showInviteCheckbox && (
        <>
          <div className='tw-mb-4 tw-flex tw-w-[100%] tw-flex-wrap tw-justify-between tw-gap-y-8'>
            <div>
              <ControlledCheckbox
                name={getNameWithPrefix("shouldBeInvited", namePrefix)}
                control={control}
                label='Invite user and grant them access to this application'
                onBlur={(checked: boolean) => {
                  // If user wants to invite a contact but hasn't inputted an email, show an error
                  const emailFieldName = getNameWithPrefix("email", namePrefix);
                  const emailVal = _.get(getValues(), emailFieldName);
                  if (checked && !emailVal)
                    setError(emailFieldName, {
                      message: "An email is required to invite this user and grant them access",
                    });
                  else clearErrors(emailFieldName);
                }}
                defaultValue={shouldBeInvited || false}
              />
              <Tooltip>
                <div>
                  Granting access allows them to view the application, view your communication with staff, and pay for
                  any fees.
                </div>
              </Tooltip>
            </div>
            {!isSubmitterSection && deleteButton}
          </div>
        </>
      )}
      {!isSubmitterSection && !showInviteCheckbox && <div> {deleteButton} </div>}
      {showDivider && <Divider />}
      {/* Save Changes Button */}
      {saveButton}
    </div>
  );
};
